const finite = value => typeof value === 'number' && Number.isFinite(value);
const name = value => typeof value === 'string' ? value.trim().toLowerCase() : '';
const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));

// A bounded territorial heuristic, not a learned probability of scoring.
// It depends only on the two pass locations, never the eventual match result.
export function territorialValue(point, team, stadium) {
  if (!finite(point?.x) || !finite(point?.y) || !['red', 'blue'].includes(team)) return null;
  const goalXs = (Array.isArray(stadium?.goals) ? stadium.goals : [])
    .flatMap(goal => [goal?.p0?.[0], goal?.p1?.[0], goal?.p0?.x, goal?.p1?.x]).filter(finite);
  const halfWidth = Math.max(1, ...(goalXs.length ? goalXs.map(Math.abs) : [Number(stadium?.width) || 800]));
  const halfHeight = Math.max(1, Number(stadium?.height) || halfWidth / 2);
  const depth = clamp((1 + point.x * (team === 'red' ? 1 : -1) / halfWidth) / 2, 0, 1);
  const centrality = 1 - clamp(Math.abs(point.y) / halfHeight, 0, 1);
  return 0.2 * depth ** 3 * (0.35 + 0.65 * centrality);
}

export function ratingPossessionEvidence(match, analysis) {
  const result = new Map();
  const get = nick => {
    const key = name(nick);
    if (!result.has(key)) result.set(key, { possessionValue: 0, pressureRelief: 0, possessionSequences: 0 });
    return result.get(key);
  };
  const seen = new Set();
  for (const pass of Array.isArray(match?.passes) ? match.passes : []) {
    const nick = pass?.player || pass?.lastShot?.player;
    const tick = pass?.lastShot?.matchTick ?? pass?.lastShot?.tick;
    const team = name(pass?.lastShot?.team ?? pass?.team);
    if (!name(nick) || !finite(tick) || !['red', 'blue'].includes(team)) continue;
    const key = `${name(nick)}:${tick}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const from = territorialValue(pass.lastShot, team, match?.stadium);
    const to = territorialValue(pass.shot, team, match?.stadium);
    const delta = from !== null && to !== null ? clamp(to - from, -0.1, 0.1) : 0;
    if (pass.lastShot?.pressured !== true && delta === 0) continue;
    const evidence = get(nick);
    if (pass.lastShot?.pressured === true) evidence.pressureRelief++;
    evidence.possessionValue += delta;
  }
  for (const possession of analysis?.possessionTimeline || []) {
    if (!possession?.players || typeof possession.players.entries !== 'function') continue;
    for (const [nick, info] of possession.players.entries()) {
      if (name(nick) && finite(info?.touches) && info.touches > 0) get(nick).possessionSequences++;
    }
  }
  return result;
}
